import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sparkline } from "@/components/Sparkline";
import { useEtfFlows } from "@/hooks/useEtfFlows";
import { formatUSD, formatPercent, toNum } from "@/lib/format";
import { TrendingUp, TrendingDown, DollarSign, Info } from "lucide-react";
import { cn } from "@/lib/utils";

type Asset = 'BTC' | 'ETH';

interface EtfFlowsCardProps {
  asset: Asset;
}

export const EtfFlowsCard = ({ asset }: EtfFlowsCardProps) => {
  const { data, isLoading, isError } = useEtfFlows(asset, '7d');

  const iconBgColor = asset === 'BTC' ? 'bg-amber-500/10' : 'bg-blue-500/10';
  const iconColor = asset === 'BTC' ? 'text-amber-500' : 'text-blue-500';

  if (isLoading) {
    return (
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center", iconBgColor)}>
              <DollarSign className={cn("h-5 w-5", iconColor)} />
            </div>
            <CardTitle>ETF Flows - {asset}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground animate-pulse">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data?.data || data.data.length === 0) {
    return (
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center", iconBgColor)}>
              <DollarSign className={cn("h-5 w-5", iconColor)} />
            </div>
            <CardTitle>ETF Flows - {asset}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Dados não disponíveis</div>
        </CardContent>
      </Card>
    );
  }

  const sortedData = [...data.data].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sortedData[sortedData.length - 1];
  const avgLast7d = sortedData.slice(0, -1).reduce((sum, d) => sum + (toNum(d.total_net_flow_usd) ?? 0), 0) / Math.max(1, sortedData.length - 1);
  const deltaVsAvg = (toNum(latest?.total_net_flow_usd) ?? 0) - avgLast7d;
  const deltaPercent = avgLast7d !== 0 ? (deltaVsAvg / Math.abs(avgLast7d)) * 100 : 0;
  
  const netFlowLatest = toNum(latest?.total_net_flow_usd) ?? 0;
  const isPositive = netFlowLatest >= 0;
  
  const sparklineData = sortedData.map(d => toNum(d.total_net_flow_usd) ?? 0);
  
  // Format the latest date for display
  const latestDate = latest?.date ? new Date(latest.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';

  return (
    <Card className="glass-card transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center", iconBgColor)}>
              <DollarSign className={cn("h-5 w-5", iconColor)} />
            </div>
            <div>
              <CardTitle className="text-base">ETF Flows - {asset}</CardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs mt-1 cursor-help">
                      <Info className="h-3 w-3 mr-1" />
                      {latestDate}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Flows de ETF são consolidados D+1 pela indústria</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <Badge 
            variant={isPositive ? "default" : "destructive"}
            className={cn(
              "text-sm font-semibold",
              isPositive ? "bg-profit/10 text-profit border-profit/20" : "bg-loss/10 text-loss border-loss/20"
            )}
          >
            {isPositive ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {formatUSD(netFlowLatest)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="p-3 rounded-lg bg-muted/30">
            <div className="text-muted-foreground text-xs mb-1">Net Flow Diário</div>
            <div className={cn("text-lg font-bold", isPositive ? 'text-profit' : 'text-loss')}>
              {formatUSD(netFlowLatest)}
            </div>
          </div>
          <div className="p-3 rounded-lg bg-muted/30">
            <div className="text-muted-foreground text-xs mb-1">vs Média 7d</div>
            <div className={cn("text-lg font-bold", deltaVsAvg >= 0 ? 'text-profit' : 'text-loss')}>
              {formatPercent(deltaPercent)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <TrendingUp className="h-3 w-3 text-profit" />
              Inflows
            </div>
            <div className="font-semibold text-profit">
              {formatUSD(latest?.total_in_usd)}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <TrendingDown className="h-3 w-3 text-loss" />
              Outflows
            </div>
            <div className="font-semibold text-loss">
              {formatUSD(latest?.total_out_usd)}
            </div>
          </div>
        </div>

        <div className="pt-2">
          <div className="text-xs text-muted-foreground mb-2">Tendência 7 Dias</div>
          <Sparkline 
            data={sparklineData} 
            width={280} 
            height={40} 
            strokeWidth={2}
          />
        </div>

        <div className="text-xs text-muted-foreground border-t border-border/50 pt-3 flex items-center justify-between">
          <span>{latest?.tickers_count || 0} ETFs monitorados</span>
          <Badge variant="outline" className="text-[10px] py-0">Dados oficiais</Badge>
        </div>
      </CardContent>
    </Card>
  );
};
